import React, { useRef, useState } from 'react';
import { fmtClock } from '../../utils/cameraRelay';
import { C } from './CamerasStyles';

export const RANGE_WORKDAY = [360, 1350]; // 6:00 AM – 10:30 PM
export const RANGE_FULL = [0, 1440];

const TICKS = {
  workday: [360, 480, 600, 720, 840, 960, 1080, 1200, 1320],
  full: [0, 180, 360, 540, 720, 900, 1080, 1260, 1440],
};

function tickLabel(m) {
  const h = Math.round(m / 60) % 24;
  if (h === 0) return '12a';
  if (h === 12) return '12p';
  return h < 12 ? `${h}a` : `${h - 12}p`;
}

export const pctOf = (m, [w0, w1]) => `${((Math.max(w0, Math.min(w1, m)) - w0) / (w1 - w0) * 100).toFixed(3)}%`;
export const widthOf = (a, b, [w0, w1]) => {
  const s = Math.max(a, w0);
  const e = Math.min(b, w1);
  return Math.max(0, (e - s) / (w1 - w0) * 100);
};

/**
 * The day as lanes (recorded footage, JobQ work per station) with a playhead.
 * Click or drag anywhere on the tracks to jump; arrow keys move a minute
 * (Shift: ten).
 *
 * lanes: [{ key, label, height, blocks: [{ start, end, color, label, title }] }]
 */
export default function DayBar({ lanes, range, playhead, now, highlight, onSeek, ariaLabel = 'Time of day' }) {
  const trackRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [w0, w1] = range;
  const ticks = range[0] === 0 ? TICKS.full : TICKS.workday;
  const head = drag ?? playhead;

  const minuteAt = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
    return w0 + f * (w1 - w0);
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    setDrag(minuteAt(e.clientX));
  };
  const onPointerMove = (e) => {
    if (drag === null) return;
    setDrag(minuteAt(e.clientX));
  };
  const onPointerUp = (e) => {
    if (drag === null) return;
    const m = minuteAt(e.clientX);
    setDrag(null);
    onSeek(m);
  };
  const onKeyDown = (e) => {
    const base = playhead ?? w0;
    const step = e.shiftKey ? 10 : 1;
    let next = null;
    if (e.key === 'ArrowLeft') next = base - step;
    else if (e.key === 'ArrowRight') next = base + step;
    else if (e.key === 'Home') next = w0;
    else if (e.key === 'End') next = w1;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.max(0, Math.min(1439.9, next)));
  };

  const showNow = now != null && now > w0 && now < w1;

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ width: 104, flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {lanes.map((l) => (
          <div key={l.key} className="jqc-ell" style={{ height: l.height, display: 'flex', alignItems: 'center', fontSize: 12, color: C.muted }}>{l.label}</div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <div
          ref={trackRef}
          className="jqc-day"
          role="slider"
          tabIndex={0}
          aria-label={ariaLabel}
          aria-valuemin={w0}
          aria-valuemax={w1}
          aria-valuenow={head == null ? undefined : Math.round(head)}
          aria-valuetext={head == null ? undefined : fmtClock(head)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => setDrag(null)}
          onKeyDown={onKeyDown}
          style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}
        >
          {lanes.map((l) => (
            <div key={l.key} style={{ position: 'relative', height: l.height, background: C.track, borderRadius: 5, overflow: 'hidden' }}>
              {l.blocks.map((b, i) => {
                const w = widthOf(b.start, b.end, range);
                if (w <= 0) return null;
                return (
                  <span
                    key={i}
                    className="jqc-blk"
                    title={b.title}
                    style={{ left: pctOf(b.start, range), width: `${w.toFixed(3)}%`, background: b.color }}
                  >
                    {b.label && w >= (l.minLabelPct ?? 2.6) ? b.label : ''}
                  </span>
                );
              })}
              {showNow && (
                <span style={{ position: 'absolute', top: 0, bottom: 0, left: pctOf(now, range), right: 0, background: '#111316' }} />
              )}
            </div>
          ))}
          {highlight && (
            <span style={{ position: 'absolute', top: -4, bottom: -4, left: pctOf(highlight[0], range), width: `${widthOf(highlight[0], highlight[1], range).toFixed(3)}%`, border: '1px solid #4a7cc0', background: 'rgba(90,162,255,.10)', borderRadius: 4, pointerEvents: 'none' }} />
          )}
          {head != null && head >= w0 && head <= w1 && (
            <span style={{ position: 'absolute', top: -7, bottom: -3, left: pctOf(head, range), width: 2, marginLeft: -1, background: '#fff', pointerEvents: 'none' }}>
              <span style={{ position: 'absolute', top: -3, left: -4, width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
              {drag != null && (
                <span style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translate(-50%, -8px)', padding: '2px 6px', borderRadius: 6, background: '#fff', color: C.bg, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {fmtClock(drag)}
                </span>
              )}
            </span>
          )}
        </div>
        <div style={{ position: 'relative', height: 18, marginTop: 6 }}>
          {ticks.map((t) => (
            <span key={t} style={{ position: 'absolute', left: pctOf(t, range), transform: 'translateX(-50%)', fontSize: 11, color: '#7d8590' }}>{tickLabel(t)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** JobQ runs → DayBar blocks. */
export function runsToBlocks(runs) {
  return (runs || []).map((r) => ({
    start: r.start,
    end: r.end,
    color: r.state === 'Working' ? C.workBlock : C.maintBlock,
    label: r.job != null ? String(r.job) : '',
    title: `${fmtClock(r.start)}–${fmtClock(r.end)} · ${r.job != null ? `Job ${r.job}${r.desc ? ` · ${r.desc}` : ''}${r.user ? ` · ${r.user}` : ''}` : 'Maintenance'}`,
  }));
}

/** Recorded stretches → DayBar blocks. */
export function coverageToBlocks(segments) {
  return (segments || []).map((s) => ({
    start: s.start,
    end: s.end,
    color: C.recorded,
    label: '',
    title: `Recorded ${fmtClock(s.start)}–${fmtClock(s.end)}`,
  }));
}
